import { useState, useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  Box,
  Typography,
  Paper,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  TextField,
  InputAdornment,
  CircularProgress
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useTheme } from '@mui/material'
import { useCorpusData } from '../../context/CorpusDataContext'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin, ChartDataLabels)

// Equipment types
const equipmentTypes = [
  { id: 'Chiller', label: 'Chiller' },
  { id: 'CRAC', label: 'CRAC' },
  { id: 'RTU', label: 'RTU' },
  { id: 'Split System', label: 'DX Split' },
  { id: 'Refrigeration', label: 'Refrigeration' },
  { id: 'Package', label: 'Package' }
]

function ManufacturerBreakdown() {
  const theme = useTheme()
  const palette = theme.palette
  // Get case studies from corpus data context
  const { caseStudies, loading } = useCorpusData()

  // Filter states
  const [selectedEquipment, setSelectedEquipment] = useState(new Set(equipmentTypes.map(e => e.id)))
  const [manufacturerSearch, setManufacturerSearch] = useState('')
  const [ageRange, setAgeRange] = useState([1, 30])
  const [capacityRange, setCapacityRange] = useState([1, 500])

  // Get all manufacturers
  const allManufacturers = useMemo(() => {
    if (!caseStudies) return []
    const mfrs = new Set()
    caseStudies.forEach(cs => {
      if (cs.manufacturer) mfrs.add(cs.manufacturer)
    })
    return Array.from(mfrs).sort()
  }, [caseStudies])

  const [selectedManufacturers, setSelectedManufacturers] = useState(new Set(allManufacturers))

  // Aggregate data by Equipment Type → Manufacturer
  const aggregatedData = useMemo(() => {
    if (!caseStudies) return []
    const data = []

    caseStudies.forEach(cs => {
      // Apply filters
      if (!cs.equipment_type || !selectedEquipment.has(cs.equipment_type)) return
      if (cs.manufacturer && !selectedManufacturers.has(cs.manufacturer)) return
      if (cs.equipment_age && (cs.equipment_age < ageRange[0] || cs.equipment_age > ageRange[1])) return
      if (cs.capacity_tons && (cs.capacity_tons < capacityRange[0] || cs.capacity_tons > capacityRange[1])) return

      const key = `${cs.equipment_type}|${cs.manufacturer || 'Unknown'}`
      let existing = data.find(d => d.key === key)

      if (!existing) {
        existing = {
          key,
          equipmentType: cs.equipment_type,
          manufacturer: cs.manufacturer || 'Unknown',
          records: 0,
          kwTonBefore: [],
          kwTonAfter: [],
          efficiencyPct: []
        }
        data.push(existing)
      }

      existing.records++
      if (cs.kw_per_ton_before) existing.kwTonBefore.push(cs.kw_per_ton_before)
      if (cs.kw_per_ton_after) existing.kwTonAfter.push(cs.kw_per_ton_after)
      if (cs.efficiency_pct) existing.efficiencyPct.push(cs.efficiency_pct)
    })

    // Calculate averages and sort
    return data
      .map(d => ({
        ...d,
        avgKwTonBefore: d.kwTonBefore.length > 0 ? d.kwTonBefore.reduce((a, b) => a + b, 0) / d.kwTonBefore.length : null,
        avgKwTonAfter: d.kwTonAfter.length > 0 ? d.kwTonAfter.reduce((a, b) => a + b, 0) / d.kwTonAfter.length : null,
        avgEfficiency: d.efficiencyPct.length > 0 ? d.efficiencyPct.reduce((a, b) => a + b, 0) / d.efficiencyPct.length : null
      }))
      .filter(d => d.records > 0)
      .sort((a, b) => a.equipmentType.localeCompare(b.equipmentType) || a.manufacturer.localeCompare(b.manufacturer))
      .slice(0, 20) // Limit to avoid chart overflow
  }, [caseStudies, selectedEquipment, selectedManufacturers, ageRange, capacityRange])

  // Calculate overall average for reference line
  const overallAvgSavings = useMemo(() => {
    const allEfficiencies = aggregatedData.filter(d => d.avgEfficiency !== null).map(d => d.avgEfficiency)
    return allEfficiencies.length > 0 ? allEfficiencies.reduce((a, b) => a + b, 0) / allEfficiencies.length : 0
  }, [aggregatedData])

  // Create labels with abbreviated manufacturer names
  const labels = aggregatedData.map(d => {
    const mfrAbbrev = d.manufacturer.slice(0, 3)
    return `${d.equipmentType.slice(0, 6)}|${mfrAbbrev}`
  })

  // kW/Ton Chart Data
  const kwTonChartData = {
    labels,
    datasets: [
      {
        label: 'Avg. Base kW Per Ton',
        data: aggregatedData.map(d => d.avgKwTonBefore),
        backgroundColor: palette.info.main,
        borderColor: palette.info.dark,
        borderWidth: 1,
        borderRadius: 2
      },
      {
        label: 'Avg. Post kW Per Ton',
        data: aggregatedData.map(d => d.avgKwTonAfter),
        backgroundColor: palette.accent?.orange || '#FF6B35',
        borderColor: palette.warning.dark,
        borderWidth: 1,
        borderRadius: 2
      }
    ]
  }

  // Savings % Chart Data
  const savingsChartData = {
    labels,
    datasets: [
      {
        label: 'Avg. Savings %',
        data: aggregatedData.map(d => d.avgEfficiency),
        backgroundColor: palette.success.main,
        borderColor: palette.success.dark,
        borderWidth: 1,
        borderRadius: 2
      }
    ]
  }

  // kW/Ton Chart Options
  const kwTonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, padding: 10, font: { size: 11 } }
      },
      title: {
        display: true,
        text: 'Avg KW/Ton',
        font: { size: 14, weight: 'bold' }
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const idx = context[0].dataIndex
            const d = aggregatedData[idx]
            return `${d.equipmentType} / ${d.manufacturer}`
          },
          afterBody: (context) => {
            const idx = context[0].dataIndex
            const d = aggregatedData[idx]
            return [`Number of Records: ${d.records}`]
          }
        }
      },
      datalabels: {
        display: (context) => context.dataset.data[context.dataIndex] !== null,
        anchor: 'end',
        align: 'top',
        offset: 2,
        font: { size: 9 },
        formatter: (value) => value ? value.toFixed(2) : ''
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 9 }, maxRotation: 45 }
      },
      y: {
        title: { display: true, text: 'kW/Ton' },
        min: 0
      }
    }
  }

  // Savings % Chart Options with Reference Line
  const savingsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, padding: 10, font: { size: 11 } }
      },
      title: {
        display: true,
        text: 'Avg Savings %',
        font: { size: 14, weight: 'bold' }
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const idx = context[0].dataIndex
            const d = aggregatedData[idx]
            return `${d.equipmentType} / ${d.manufacturer}`
          },
          afterBody: (context) => {
            const idx = context[0].dataIndex
            const d = aggregatedData[idx]
            return [`Number of Records: ${d.records}`]
          }
        }
      },
      annotation: {
        annotations: {
          averageLine: {
            type: 'line',
            yMin: overallAvgSavings,
            yMax: overallAvgSavings,
            borderColor: palette.error.main,
            borderWidth: 2,
            borderDash: [6, 6],
            label: {
              display: true,
              content: `Avg: ${overallAvgSavings.toFixed(1)}%`,
              position: 'end',
              backgroundColor: `rgba(229, 62, 62, 0.8)`,
              color: 'white',
              font: { size: 10 }
            }
          }
        }
      },
      datalabels: {
        display: (context) => context.dataset.data[context.dataIndex] !== null,
        anchor: 'end',
        align: 'top',
        offset: 2,
        font: { size: 9 },
        formatter: (value) => value ? `${value.toFixed(1)}%` : ''
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 9 }, maxRotation: 45 }
      },
      y: {
        title: { display: true, text: 'Avg. Saving Percent' },
        min: 0,
        max: 80
      }
    }
  }

  // Filter functions
  const toggleEquipment = (id) => {
    setSelectedEquipment(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  const toggleAllEquipment = () => {
    if (selectedEquipment.size === equipmentTypes.length) {
      setSelectedEquipment(new Set())
    } else {
      setSelectedEquipment(new Set(equipmentTypes.map(e => e.id)))
    }
  }

  const filteredManufacturers = allManufacturers.filter(m =>
    m.toLowerCase().includes(manufacturerSearch.toLowerCase())
  )

  const toggleManufacturer = (mfr) => {
    setSelectedManufacturers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(mfr)) newSet.delete(mfr)
      else newSet.add(mfr)
      return newSet
    })
  }

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {/* Main Charts Area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Performance by Equipment / Manufacturer
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Equipment Type / Manufacturer breakdown with overall average reference
        </Typography>

        {/* kW/Ton Chart */}
        <Box sx={{ height: 220, mb: 3 }}>
          <Bar data={kwTonChartData} options={kwTonOptions} />
        </Box>

        {/* Savings % Chart */}
        <Box sx={{ height: 220 }}>
          <Bar data={savingsChartData} options={savingsOptions} />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Red dashed line shows overall average savings ({overallAvgSavings.toFixed(1)}%).
          Values above the line indicate better-than-average performance.
        </Typography>
      </Box>

      {/* Filter Panel - Right Side */}
      <Paper
        elevation={0}
        sx={{
          width: 130,
          flexShrink: 0,
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          alignSelf: 'flex-start'
        }}
      >
        {/* Equipment Type Filter */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Equipment Type
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selectedEquipment.size === equipmentTypes.length}
                indeterminate={selectedEquipment.size > 0 && selectedEquipment.size < equipmentTypes.length}
                onChange={toggleAllEquipment}
              />
            }
            label={<Typography variant="body2">(All)</Typography>}
          />
          {equipmentTypes.map(eq => (
            <FormControlLabel
              key={eq.id}
              control={
                <Checkbox
                  size="small"
                  checked={selectedEquipment.has(eq.id)}
                  onChange={() => toggleEquipment(eq.id)}
                />
              }
              label={<Typography variant="body2">{eq.label}</Typography>}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Manufacturer Filter */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Manufacturer
        </Typography>
        <TextField
          size="small"
          placeholder="Search..."
          value={manufacturerSearch}
          onChange={(e) => setManufacturerSearch(e.target.value)}
          fullWidth
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'grey.500' }} />
              </InputAdornment>
            )
          }}
        />
        <FormGroup>
          {filteredManufacturers.slice(0, 6).map(mfr => (
            <FormControlLabel
              key={mfr}
              control={
                <Checkbox
                  size="small"
                  checked={selectedManufacturers.has(mfr)}
                  onChange={() => toggleManufacturer(mfr)}
                />
              }
              label={<Typography variant="body2" noWrap>{mfr}</Typography>}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Age Range Slider */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Avg. Unit Age
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={ageRange}
            onChange={(e, newValue) => setAgeRange(newValue)}
            min={1}
            max={30}
            valueLabelDisplay="auto"
            size="small"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Legend */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Measure Names
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'info.main', mr: 1 }} />
          <Typography variant="caption">Base kW/Ton</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', mr: 1 }} />
          <Typography variant="caption">Post kW/Ton</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', mr: 1 }} />
          <Typography variant="caption">Savings %</Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default ManufacturerBreakdown
